import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

/**
 * A calendar date picker that always renders Sunday first / Saturday last,
 * the same for every user regardless of browser or OS.
 *
 * The native <input type="date">'s calendar popup is rendered by the browser
 * itself, and its day-of-week order follows the browser's resolved locale for
 * that element. Chromium honours a per-element lang="en-US" override to force
 * Sunday-first; Firefox and Safari don't reliably follow that override and
 * fall back to the OS/browser's own region setting - so the same "fix" shows
 * correctly for one person and not another purely by which browser they're
 * on (Monday 12769220328, 2026-08-13: confirmed working for one reviewer,
 * "still the same" for the person who raised the request). Rendering the
 * calendar grid ourselves removes the browser as a variable entirely.
 *
 * Wire-format value/onChange stay the same as the native input this replaces:
 * value is a 'yyyy-mm-dd' string (or ''), onChange receives that same string.
 */
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad2 = (n) => String(n).padStart(2, '0');
const toIso = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

function parseIso(value) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { year: y, month: m - 1, day: d };
}

function formatDisplay(value) {
  const parsed = parseIso(value);
  if (!parsed) return '';
  return `${pad2(parsed.day)}/${pad2(parsed.month + 1)}/${parsed.year}`;
}

export default function DateInput({ value, onChange, min, className = '', placeholder = 'Select a date' }) {
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);
  const minParsed = parseIso(min);
  const today = new Date();
  const [viewYear, setViewYear] = useState(selected?.year ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.month ?? today.getMonth());
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPicker = () => {
    setViewYear(selected?.year ?? today.getFullYear());
    setViewMonth(selected?.month ?? today.getMonth());
    setOpen(true);
  };

  const isBeforeMin = (y, m, d) => {
    if (!minParsed) return false;
    if (y !== minParsed.year) return y < minParsed.year;
    if (m !== minParsed.month) return m < minParsed.month;
    return d < minParsed.day;
  };

  const pickDay = (y, m, d) => {
    if (isBeforeMin(y, m, d)) return;
    onChange(toIso(y, m, d));
    setOpen(false);
  };

  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startWeekday = firstOfMonth.getDay(); // 0 = Sunday
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) {
    const d = daysInPrevMonth - startWeekday + 1 + i;
    cells.push({ day: d, inMonth: false, y: viewMonth === 0 ? viewYear - 1 : viewYear, m: viewMonth === 0 ? 11 : viewMonth - 1 });
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ day: d, inMonth: true, y: viewYear, m: viewMonth });
  }
  while (cells.length % 7 !== 0) {
    const d = cells.length - (startWeekday + daysInMonth) + 1;
    cells.push({ day: d, inMonth: false, y: viewMonth === 11 ? viewYear + 1 : viewYear, m: viewMonth === 11 ? 0 : viewMonth + 1 });
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={openPicker}
        className={className || 'w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm text-left flex items-center justify-between gap-2 bg-white'}
      >
        <span className={value ? '' : 'text-gray-400'}>{value ? formatDisplay(value) : placeholder}</span>
        <CalendarIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-64">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => changeMonth(-1)} className="p-1 rounded hover:bg-gray-100">
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-sm font-medium text-gray-700">{MONTH_LABELS[viewMonth]} {viewYear}</span>
            <button type="button" onClick={() => changeMonth(1)} className="p-1 rounded hover:bg-gray-100">
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-y-1 text-center">
            {WEEKDAY_LABELS.map((label) => (
              <span key={label} className="text-[11px] font-medium text-gray-400">{label}</span>
            ))}
            {cells.map(({ day, inMonth, y, m }, idx) => {
              const isSelected = selected && selected.year === y && selected.month === m && selected.day === day;
              const isToday = today.getFullYear() === y && today.getMonth() === m && today.getDate() === day;
              const disabled = isBeforeMin(y, m, day);
              return (
                <button
                  type="button"
                  key={`${y}-${m}-${day}-${idx}`}
                  onClick={() => pickDay(y, m, day)}
                  disabled={disabled}
                  className={[
                    'text-xs rounded-full w-7 h-7 mx-auto flex items-center justify-center',
                    !inMonth ? 'text-gray-300' : 'text-gray-700',
                    disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-teal-50 cursor-pointer',
                    isSelected ? 'bg-teal-600 text-white hover:bg-teal-600' : '',
                    isToday && !isSelected ? 'border border-teal-400' : '',
                  ].join(' ')}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
